import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Header } from '@/components/layout/Header';
import { UserJourney } from '@/components/shared/UserJourney';
import { Button } from '@/components/shared/Button';
import { ArrowRight, Lock, Loader2 } from 'lucide-react';
import { ENTITY_CONFIGS } from '@/lib/entity-config';
import { cn } from '@/lib/cn';

const API_BASE_URL = 'https://al3viewer-api.lancetkeith.workers.dev';

interface LineageData {
  parent: string;
  variant: string;
  owner: string;
}

interface LicenseType {
  license_id: string;
  license_type: string;
  price_eth: number;
  price_usd: number;
  usage_rights: string;
}

export function AL3VLedger() {
  const entity = ENTITY_CONFIGS.al3viewer;
  const navigate = useNavigate();
  const location = useLocation();

  const [assetId, setAssetId] = useState<string>('');
  const [assetName, setAssetName] = useState<string>('Loading...');
  const [assetImageUrl, setAssetImageUrl] = useState<string>('');
  const [lineage, setLineage] = useState<LineageData | null>(null);
  const [_licenseTypes, setLicenseTypes] = useState<LicenseType[]>([]);
  const [selectedLicense, setSelectedLicense] = useState<LicenseType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Get asset data from route state (passed from FDAIA)
    const state = location.state as { asset_id?: string; variant?: any } | null;

    if (state?.asset_id) {
      setAssetId(state.asset_id);
      if (state.variant) {
        setAssetName(state.variant.av_id || 'Footwear Variant');
        setAssetImageUrl(state.variant.image_url || '');
      }
      fetchAssetData(state.asset_id);
    } else {
      setError('No asset selected. Please generate a variant from FDAIA first.');
      setLoading(false);
    }
  }, [location]);

  const fetchAssetData = async (asset_id: string) => {
    try {
      setLoading(true);

      // Fetch lineage data
      const lineageResponse = await fetch(`${API_BASE_URL}/api/al3viewer/lineage/${asset_id}`);
      if (lineageResponse.ok) {
        const lineageData = await lineageResponse.json();
        setLineage({
          parent: lineageData.prototype_id || 'Template_Sport_v2',
          variant: lineageData.variant_id || 'CyberKnit_Custom',
          owner: lineageData.user_id || 'User_0x4A8F'
        });
      }

      // Fetch license types
      const licensesResponse = await fetch(`${API_BASE_URL}/api/al3viewer/licenses`);
      if (licensesResponse.ok) {
        const licensesData = await licensesResponse.json();
        if (licensesData.success && licensesData.licenses) {
          setLicenseTypes(licensesData.licenses);
          // Default to Commercial license
          const commercial = licensesData.licenses.find((l: LicenseType) => l.license_type === 'COMMERCIAL');
          setSelectedLicense(commercial || licensesData.licenses[0]);
        }
      }

      setLoading(false);
    } catch (err) {
      console.error('Error fetching asset data:', err);
      setError('Failed to load asset data');
      setLoading(false);
    }
  };

  const handleConfirmPayment = async () => {
    if (!selectedLicense || !assetId) return;

    try {
      // Call purchase API
      console.log('Calling purchase API with:', {
        asset_id: assetId,
        user_id: 'user-temp-id',
        license_id: selectedLicense.license_id,
        wallet_address: '0x4A8F...92E3'
      });
      const response = await fetch(`${API_BASE_URL}/api/al3viewer/purchase`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          asset_id: assetId,
          user_id: 'user-temp-id', // Fixed: was owner_user_id, should be user_id
          license_id: selectedLicense.license_id, // Fixed: was license_type, should be license_id
          payment_method: 'metamask',
          wallet_address: '0x4A8F...92E3' // Added: required by API
        })
      });

      console.log('Purchase API response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Purchase API error:', errorText);
        throw new Error(`Purchase API returned ${response.status}: ${errorText}`);
      }

      const data = await response.json();
      console.log('Purchase API response data:', data);

      if (data.success) {
        // Set cookie to track purchase
        document.cookie = `al3v_purchase=true; path=/; max-age=${60 * 60 * 24 * 30}`; // 30 days
        // Navigate to M-VC with purchase confirmation and asset details
        navigate('/mvc', {
          state: {
            purchased_asset_id: assetId,
            license_type: selectedLicense.license_type,
            purchased_asset: {
              id: assetId,
              asset_id: assetId,
              image_url: assetImageUrl,
              name: assetName,
              category: 'Sneakers' // Could be extracted from prototype data if available
            }
          }
        });
      } else {
        setError(`Purchase failed: ${data.message || 'Please try again.'}`);
      }
    } catch (err) {
      console.error('Error processing purchase:', err);
      const errorMessage = err instanceof Error ? err.message : 'Payment processing failed';
      setError(errorMessage);
    }
  };

  if (loading) {
    return (
      <div className="flex h-screen flex-col bg-ck-dark">
        <Header entity={entity} onClose={() => navigate('/')} />
        <UserJourney currentStep="al3viewer" sessionId="2024-0847" />
        <div className="flex flex-1 items-center justify-center">
          <div className="text-center">
            <Loader2 className="mx-auto h-12 w-12 animate-spin text-ck-cyan" />
            <p className="mt-4 text-lg font-medium text-muted-foreground">Loading asset data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-screen flex-col bg-ck-dark">
        <Header entity={entity} onClose={() => navigate('/')} />
        <UserJourney currentStep="al3viewer" sessionId="2024-0847" />
        <div className="flex flex-1 items-center justify-center">
          <div className="text-center">
            <p className="text-lg font-medium text-red-400">{error}</p>
            <Button variant="gradient-purple" size="lg" className="mt-4" onClick={() => navigate('/fdaia')}>
              Return to Design Canvas
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen flex-col bg-ck-dark">
      {/* Header */}
      <Header entity={entity} onClose={() => navigate('/')} />

      {/* User Journey Nav */}
      <UserJourney currentStep="al3viewer" sessionId="2024-0847" />

      {/* Responsive: True vertical stack on mobile, side-by-side on desktop */}
      <div className="flex flex-1 flex-col lg:grid lg:grid-cols-2 lg:overflow-hidden">
        {/* Left Panel - Asset Preview & Lineage */}
        <div className="flex-shrink-0 overflow-y-auto border-b bg-ck-dark p-4 md:p-6 lg:border-b-0 lg:border-r lg:border-ck-dark-border lg:p-8">
          {/* Asset Preview */}
          <div className="rounded-lg border border-ck-cyan bg-ck-dark-card p-6 glow-cyan">
            <div className="mb-4 flex items-center justify-between">
              <span className="font-mono text-xs text-ck-cyan">ASSET ID: {assetId}</span>
            </div>

            <div className="mb-6 rounded-lg overflow-hidden bg-gradient-to-br from-ck-cyan/20 to-ck-purple/20">
              {assetImageUrl ? (
                <img
                  src={assetImageUrl}
                  alt={assetName}
                  className="w-full h-auto object-contain"
                />
              ) : (
                <div className="flex h-48 items-center justify-center md:h-56 lg:h-64">
                  <div className="text-center">
                    <p className="text-lg font-medium text-ck-cyan md:text-xl">{assetName}</p>
                    <p className="mt-2 text-xs text-muted-foreground md:text-sm">3D Asset Viewer</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Asset Lineage Trace */}
          <div className="mt-8">
            <div className="mb-4 flex items-center gap-2">
              <div className="h-2 w-2 rounded-full bg-ck-purple" />
              <h3 className="text-sm font-semibold uppercase tracking-wider text-ck-purple">
                Asset Lineage Trace
              </h3>
            </div>

            <div className="space-y-3">
              {lineage && [
                { label: 'PARENT', value: lineage.parent, color: 'purple' },
                { label: 'VARIANT', value: lineage.variant, color: 'cyan' },
                { label: 'OWNER', value: lineage.owner, color: 'blue' },
              ].map((item, idx) => (
                <div key={idx} className="flex items-center gap-3">
                  <div
                    className={cn(
                      'flex-1 rounded-lg border px-4 py-3',
                      item.color === 'purple' && 'border-ck-purple bg-ck-purple/10',
                      item.color === 'cyan' && 'border-ck-cyan bg-ck-cyan/10',
                      item.color === 'blue' && 'border-blue-500 bg-blue-500/10'
                    )}
                  >
                    <div className="text-xs text-muted-foreground">{item.label}</div>
                    <div className="font-mono text-sm font-medium text-foreground">
                      {item.value}
                    </div>
                  </div>
                  {idx < 2 && <ArrowRight className="h-4 w-4 text-ck-cyan" />}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right Panel - Payment Flow */}
        <div className="flex-1 overflow-y-auto bg-ck-dark-card p-4 md:p-6 lg:p-8">
          <div className="mb-4 flex items-center justify-between md:mb-6">
            <h2 className="text-xl font-bold text-ck-cyan md:text-2xl">PAYMENT</h2>
            <span className="rounded-full bg-ck-cyan/20 px-2 py-1 text-xs font-medium text-ck-cyan md:px-3">
              STEP 2/3
            </span>
          </div>

          {/* License Selection */}
          <div className="mb-6 space-y-4 rounded-lg border border-ck-cyan bg-ck-dark p-6">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">License Selected</span>
              <span className="text-lg font-bold text-ck-cyan">{selectedLicense?.license_type || 'COMMERCIAL'}</span>
            </div>
            <div className="flex items-center justify-between border-t border-ck-dark-border pt-4">
              <span className="text-sm text-muted-foreground">Asset ID</span>
              <span className="font-mono text-sm text-ck-purple">{assetId}</span>
            </div>
            <div className="flex items-center justify-between border-t border-ck-dark-border pt-4">
              <span className="text-lg font-semibold text-foreground">Total Amount</span>
              <div className="text-right">
                <div className="text-2xl font-bold text-ck-cyan">{selectedLicense?.price_eth || 0.25} ETH</div>
                <div className="text-xs text-muted-foreground">≈ ${selectedLicense?.price_usd?.toFixed(2) || '467.50'} USD</div>
              </div>
            </div>
          </div>

          {/* Wallet Address */}
          <div className="mb-6">
            <label className="mb-2 block text-sm font-semibold uppercase tracking-wider text-ck-purple">
              Wallet Address
            </label>
            <input
              type="text"
              value="0x4A8F...92E3"
              readOnly
              className="w-full rounded-lg border border-ck-dark-border bg-ck-dark px-4 py-3 font-mono text-sm text-ck-cyan"
            />
          </div>

          {/* Payment Method */}
          <div className="mb-6">
            <label className="mb-2 block text-sm font-semibold uppercase tracking-wider text-ck-purple">
              Payment Method
            </label>
            <div className="space-y-2">
              <button className="flex w-full items-center justify-between rounded-lg border-2 border-ck-purple bg-ck-purple/10 p-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded bg-orange-500">
                    <span className="text-xs font-bold">🦊</span>
                  </div>
                  <span className="font-medium text-foreground">MetaMask</span>
                </div>
                <div className="h-5 w-5 rounded-full border-4 border-ck-purple bg-transparent" />
              </button>
              <button className="flex w-full items-center justify-between rounded-lg border border-ck-dark-border bg-ck-dark p-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded bg-blue-500">
                    <span className="text-xs font-bold">💼</span>
                  </div>
                  <span className="font-medium text-muted-foreground">WalletConnect</span>
                </div>
                <div className="h-5 w-5 rounded-full border border-ck-dark-border bg-transparent" />
              </button>
            </div>
          </div>

          {/* Gas Fee Notice */}
          <div className="mb-6 rounded-lg border border-ck-cyan/30 bg-ck-cyan/5 p-4">
            <p className="text-xs text-ck-cyan">
              ⚠️ Gas fees will be calculated and added at confirmation. Estimated: ~0.003 ETH
            </p>
          </div>

          {/* Action Buttons */}
          <div className="space-y-3">
            <Button variant="gradient-purple" size="lg" className="w-full" onClick={handleConfirmPayment}>
              CONFIRM PAYMENT
            </Button>
            <Button variant="outline" size="lg" className="w-full" onClick={() => navigate('/fdaia')}>
              ← BACK TO DESIGN
            </Button>
          </div>

          {/* Security Badge */}
          <div className="mt-6 rounded-lg border border-ck-cyan/30 bg-ck-dark p-4 text-center">
            <div className="flex items-center justify-center gap-2 text-ck-cyan">
              <Lock className="h-4 w-4" />
              <span className="text-xs font-medium uppercase tracking-wider">
                Secure Transaction
              </span>
            </div>
            <p className="mt-1 text-xs text-ck-purple">256-BIT ENCRYPTION</p>
          </div>
        </div>
      </div>
    </div>
  );
}